import { FC } from 'react';
import { useIntl } from 'react-intl';
import { Line } from 'react-chartjs-2';
import { ChartData, ChartDataset, ChartOptions } from 'chart.js';

import {
  ACCENT_COLOR,
  GRID_COLOR,
  HIGHLIGHT_COLOR,
  NEUTRAL_COLOR,
  SURFACE_COLOR,
} from '../../constants/subscriberCohorts';
import {
  CohortCell,
  RetentionMode,
  SubscriberCohortsReport,
  formatEuro,
  formatInteger,
  formatPercent,
  maxOffset,
  monthKey,
} from '../../models/SubscriberCohortsModel';

// Plot height plus the x-axis and legend bands.
const CHART_HEIGHT_PX = 340;
const LINE_WIDTH_PX = 2;
const CONTEXT_LINE_WIDTH_PX = 1;
const POINT_RADIUS_PX = 4;
const POINT_RING_PX = 2;
// Hex alpha for the context lines: present, but behind the two that matter.
const CONTEXT_ALPHA = '99';

export type CurveMetric = 'retention' | 'ltv';

const TITLE_ID: Record<CurveMetric, string> = {
  retention: 'retentionCurve',
  ltv: 'ltvCurve',
};

type Props = {
  report: SubscriberCohortsReport;
  metric: CurveMetric;
  mode: RetentionMode;
  highlighted: string | null;
};

// The month in progress is left out: it would drag every line down at its
// right edge.
function curveValue(
  cell: CohortCell,
  metric: CurveMetric,
  mode: RetentionMode
): number | null {
  if (cell.partial) return null;
  return metric === 'retention' ? cell.retention[mode] : cell.ltv;
}

// One line per month offset (M0, M1...). Emphasis form: the weighted average
// carries the accent, the highlighted cohort the second hue, and every other
// cohort stays as thin neutral context.
const CohortCurveChart: FC<Props> = ({ report, metric, mode, highlighted }) => {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({ id: `subscriberCohorts.${id}` }, values);

  const offsets = Array.from(
    { length: maxOffset(report.cohorts) + 1 },
    (_, offset) => offset
  );
  const format = (value: number) =>
    metric === 'retention' ? formatPercent(value) : formatEuro(value, true);
  const series = (cells: CohortCell[]) =>
    offsets.map((offset) =>
      offset < cells.length ? curveValue(cells[offset], metric, mode) : null
    );

  const cohorts = report.cohorts.filter((c) => c.size > 0);
  const contextLabel = t('otherCohorts');
  const contextDatasets: ChartDataset<'line', (number | null)[]>[] = cohorts
    .filter((c) => monthKey(c.month) !== highlighted)
    .map((c) => ({
      label: contextLabel,
      data: series(c.cells),
      borderColor: `${NEUTRAL_COLOR}${CONTEXT_ALPHA}`,
      backgroundColor: NEUTRAL_COLOR,
      borderWidth: CONTEXT_LINE_WIDTH_PX,
      pointRadius: 0,
      pointHoverRadius: 0,
      order: 2,
    }));
  const highlightedCohort = cohorts.find(
    (c) => monthKey(c.month) === highlighted
  );
  const emphasisPoint = (color: string) => ({
    borderColor: color,
    backgroundColor: color,
    borderWidth: LINE_WIDTH_PX,
    pointRadius: POINT_RADIUS_PX,
    pointHoverRadius: POINT_RADIUS_PX + 1,
    pointBorderColor: SURFACE_COLOR,
    pointBorderWidth: POINT_RING_PX,
  });
  const averageByOffset = new Map(report.average.map((a) => [a.offset, a]));
  const averageDataset: ChartDataset<'line', (number | null)[]> = {
    label: t('weightedAverage'),
    data: offsets.map((offset) => {
      const average = averageByOffset.get(offset);
      if (!average) return null;
      return metric === 'retention' ? average.retention[mode] : average.ltv;
    }),
    ...emphasisPoint(ACCENT_COLOR),
    order: 0,
  };
  const datasets = [
    averageDataset,
    ...(highlightedCohort
      ? [
          {
            label: t('cohortLabel', {
              month: monthKey(highlightedCohort.month),
            }),
            data: series(highlightedCohort.cells),
            ...emphasisPoint(HIGHLIGHT_COLOR),
            order: 1,
          },
        ]
      : []),
    ...contextDatasets,
  ];
  const firstContextIndex = datasets.length - contextDatasets.length;

  const data: ChartData<'line', (number | null)[]> = {
    labels: offsets.map((offset) => `M${offset}`),
    datasets,
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    spanGaps: false,
    interaction: { mode: 'index', intersect: false },
    scales: {
      x: { grid: { display: false } },
      y: {
        min: 0,
        ...(metric === 'retention' ? { max: 1 } : {}),
        grid: { color: GRID_COLOR },
        ticks: {
          callback: (value) =>
            metric === 'retention'
              ? formatPercent(Number(value))
              : formatEuro(Number(value)),
        },
      },
    },
    plugins: {
      legend: {
        position: 'bottom',
        // The context lines share one legend entry; hiding lines by clicking
        // the legend would leave the emphasis without its context.
        onClick: () => undefined,
        labels: {
          usePointStyle: true,
          pointStyle: 'line',
          filter: (item) =>
            item.datasetIndex !== undefined &&
            item.datasetIndex <= firstContextIndex,
        },
      },
      tooltip: {
        // Only the lines in focus: listing every context cohort would bury
        // them.
        filter: (item) => item.datasetIndex < firstContextIndex,
        callbacks: {
          label: (item) => `${item.dataset.label}: ${format(item.parsed.y)}`,
          afterBody: (items) => {
            const average = averageByOffset.get(items[0]?.dataIndex ?? -1);
            if (!average) return '';
            return t('averageBase', {
              cohorts: average.cohorts,
              subscribers: formatInteger(average.subscribers),
            });
          },
        },
      },
    },
  };

  return (
    <div>
      <div className="fs-5 fw-bold mb-3">{t(TITLE_ID[metric])}</div>
      <div style={{ height: `${CHART_HEIGHT_PX}px` }}>
        <Line data={data} options={options} />
      </div>
    </div>
  );
};

export default CohortCurveChart;
